import {
  getLogoPrice,
  getCustomColorPrice,
  getBaseModel,
  getComponent,
  getOption,
  getColor,
  getTranslation,
  getElechicLogoPrice
} from './bike-schema'
import { encode as encodeQueryParams } from './query-params'
import { absUrl } from './abs-url'
import { useUrlReplacers } from './url'
import { t } from './i18n'
import { BACKEND_URL } from './settings'

const DEFAULT_RAL = 9005
const DEFAULT_BACKGROUND_COLOR = '#eee'

export function bikeConfigFromParams(params) {
  useUrlReplacers() // check for old urls which differs from wordpress and replace them
  const baseModelId = params.base_model_id
  const angle = params.angle || 'none'
  let baseModel = getBaseModel(baseModelId)
  if (!baseModel) {
    throw new Error(`Base model '${baseModelId}' not found.`)
  }
  const preset = baseModel.presets[0] || {}

  let choices = baseModel.components.reduce((choices, component) => {
    let componentId = component.id
    let v = params[componentId]

    // Use default from first preset if not set
    if (typeof v === 'undefined') {
      v = preset[componentId]
    }

    // Try to parse option ID and RAL from the raw string
    let optionId
    let ral
    let logo
    let logo_right
    let logo_left
    let logo_front

    if (v) {
      ;[optionId, ral, logo, logo_right, logo_left, logo_front] = v.split(',')
    }

    // Get the option
    let option
    if (optionId) {
      // If an option was set, then we use it
      option = getOption(component, optionId)
      if (!option) {
        throw new Error(
          `Option '${optionId}' for component '${componentId}' for base model '${baseModelId}' not found.`
        )
      }
    } else {
      // If not option was set, then take the default option or the first option
      option =
        component.options.find((option) => option.default) ||
        component.options[0]
    }

    // Add choice
    let choice = {
      componentId,
      optionId: option.id
    }
    choices.push(choice)

    // If option uses custom coloring, then we use the given one or take the default
    if (option.custom_color) {
      ral = parseInt(ral, 10)
      let color = getColor(ral)
      choice.ral = color ? color.ral : DEFAULT_RAL
    }

    if (option.id === 'unpainted') {
      choice.ral = 2
    }

    // Add logos
    if (logo) {
      choice.logo = absUrl(logo)
    }
    if (logo_right) {
      choice.logo_right = absUrl(logo_right)
    }
    if (logo_left) {
      choice.logo_left = absUrl(logo_left)
    }
    if (logo_front) {
      choice.logo_front = absUrl(logo_front)
    }

    return choices
  }, [])

  return updateConfig(
    {
      baseModelId,
      angle,
      background: parseBackground(baseModel, params.background),
      choices
    },
    {}
  )
}

function parseBackground(baseModel, background) {
  if (!background && baseModel.presets.length) {
    background = baseModel.presets[0]['background']
  }
  let [color, image, mode] = (background || '').split(',')
  color = color || DEFAULT_BACKGROUND_COLOR
  image = image && absUrl(image)
  mode = mode || 'cover'
  return { color, image, mode }
}

export function bikeConfigToQuery({
  baseModelId,
  background,
  choices,
  initials,
  angle
}) {
  const baseModel = getBaseModel(baseModelId)
  const params = {
    angle: angle || 'none',
    base_model_id: baseModelId,
    background: `${background.color},${background.image || ''},${
      background.mode
    }`,
    initials
  }
  choices
    .slice(0)
    .sort((a, b) => a.componentId.localeCompare(b.componentId))
    .forEach((choice) => {
      const component = getComponent(baseModel, choice.componentId)
      const option = getOption(component, choice.optionId)
      const v =
        `${option.id},${choice.ral || ''},${choice.logo || ''},${choice.logo_right || ''},${choice.logo_left || ''},${choice.logo_front || ''}`.replace(
          /,+$/,
          ''
        )
      params[component.id] = v
    })
  return encodeQueryParams(params)
}

export function getChoice(bikeConfig, component) {
  if (typeof component == 'string') {
    const baseModel = getBaseModel(bikeConfig.baseModelId)
    component = getComponent(baseModel, component)
  }
  const choice = bikeConfig.choices.find((choice) => {
    if (component == null) return
    return choice.componentId === component.id
  })
  if (!choice) {
    return null
  }
  return {
    option: getOption(component, choice.optionId),
    color: choice.ral && getColor(choice.ral),
    logo: choice.logo,
    logo_right: choice.logo_right,
    logo_left: choice.logo_left,
    logo_front: choice.logo_front
  }
}

export function putChoice(bikeConfig, componentId, changes) {
  const baseModel = getBaseModel(bikeConfig.baseModelId)
  // Copy the choices array and put the new choice
  let choices = bikeConfig.choices.slice(0)
  putChoiceHelper(choices, baseModel, componentId, changes, [])
  // Merge into bike config
  if (bikeConfig.baseModelId.startsWith('urban')) {
    if (bikeConfig.baseModelId.endsWith('gents')) {
      if (componentId === 'rear-carrier') {
        choices[7].ral = choices[0].ral
      }
      if (componentId === 'front-carrier') {
        choices[8].ral = choices[0].ral
      }
      if (componentId === 'alubasket') {
        choices[9].ral = choices[0].ral
      }
    } else {
      if (componentId === 'rear-carrier') {
        choices[6].ral = choices[0].ral
      }
      if (componentId === 'front-carrier') {
        choices[7].ral = choices[0].ral
      }
      if (componentId === 'alubasket') {
        choices[8].ral = choices[0].ral
      }
    }
  }

  const changed = changes.optionId

  const f = (componentId) => {
    const choice = choices.find((c) => c.componentId === componentId)
    if (choice) return choice.optionId
  }
  const i = (componentId) =>
    choices.findIndex((c) => c.componentId === componentId)

  // @cc => current choices
  const cc = {
    frame: f('frame'),
    frameType: f('frame-type'),
    frameSize: f('frame-size'),
    fork: f('fork'),
    adPlate: f('rear-axd-plate'),
    bottle: f('bottle-front'),
    guard: f('mud-guards'),
    straps: f('straps'),
    rcarrier: f('rear-carrier'),
    fcarrier: f('front-carrier'),
    basket: f('alubasket'),
    skirt: f('skirt-guard'),
    wheels: f('wheels')
  }

  // @ci => current indexes
  const ci = {
    frame: i('frame'),
    frameType: i('frame-type'),
    frameSize: i('frame-size'),
    fork: i('fork'),
    chain: i('chain-lock'),
    rcarrier: i('rear-carrier'),
    adPlate: i('rear-axd-plate'),
    straps: i('straps'),
    basket: i('alubasket'),
    skirt: i('skirt-guard'),
    wheels: i('wheels')
  }

  if (
    baseModel.id.endsWith('vintage-racer-gents') ||
    baseModel.id.endsWith('vintage-racer-ladies')
  ) {
    // chain-lock specific case
    if (
      componentId === 'chain-lock' &&
      cc.guard == 'custom' &&
      changed !== 'off'
    ) {
      choices[ci.chain].optionId = 'leather2'
    }

    if (
      componentId === 'mud-guards' &&
      cc.guard == 'off' &&
      choices[ci.chain].optionId == 'leather2'
    ) {
      choices[ci.chain].optionId = 'leather'
    }

    if (
      componentId == 'mud-guards' &&
      cc.guard == 'custom' &&
      choices[ci.chain].optionId == 'leather'
    ) {
      choices[ci.chain].optionId = 'leather2'
    }
  } else {
    // rear-carrier and skirt-guard specific cases
    if (
      componentId === 'rear-carrier' &&
      cc.skirt !== 'off' &&
      cc.rcarrier == 'custom'
    ) {
      choices[ci.rcarrier].optionId = 'custom2'
    }

    if (componentId === 'rear-carrier' && cc.rcarrier == 'off' && cc.adPlate) {
      choices[ci.adPlate].optionId = 'off'
    }
  }

  // bottle-front with large baskets specific cases
  if (
    baseModel.id.endsWith('arrow-classic-ladies') ||
    baseModel.id.endsWith('kopenhagen-balloon-ladies')
  ) {
    if (componentId === 'bottle-front') {
      if (changes.optionId === 'leather' && cc.basket == 'large') {
        choices[ci.basket].optionId = 'blarge'
      }
      if (changes.optionId === 'off' && cc.basket == 'blarge') {
        choices[ci.basket].optionId = 'large'
      }
    }
    if (
      componentId === 'alubasket' &&
      changed === 'large' &&
      cc.bottle === 'leather'
    ) {
      choices[ci.basket].optionId = 'blarge'
    }
  }

  // straps on front carriers specific cases

  if (componentId === 'front-carrier' && cc.straps !== 'off') {
    if (changes.optionId === 'custom') {
      choices[ci.straps].optionId = 'leather'
    }
    if (changes.optionId === 'low') {
      choices[ci.straps].optionId = 'low'
    }
    if (changes.optionId === 'high') {
      choices[ci.straps].optionId = 'high'
    }
    if (changes.optionId === 'off') {
      choices[ci.straps].optionId = 'off'
    }
  }

  if (componentId === 'straps' && changed !== 'off') {
    if (cc.fcarrier === 'low') choices[ci.straps].optionId = 'low'
    if (cc.fcarrier === 'high') choices[ci.straps].optionId = 'high'
    if (cc.fcarrier === 'custom') choices[ci.straps].optionId = 'leather'
  }

  if (componentId === 'wheels' && changed === 'unpainted') {
    choices[ci.wheels].ral = 2
  }

  return updateConfig(bikeConfig, {
    choices
  })
}

function putChoiceHelper(choices, baseModel, componentId, changes, stack) {
  // Avoid circular requirements
  if (stack.includes(componentId)) {
    console.error(
      `Circular requirements detected. Stack: ${stack.join(
        ', '
      )}. Changes: ${JSON.stringify(changes)}`
    )
    return
  }
  stack.push(componentId)

  // Get objects
  const component = getComponent(baseModel, componentId)
  const currentChoice = choices.find((c) => c.componentId === componentId) || {
    componentId
  }

  // Create new choice object
  const choice = {
    ...currentChoice,
    ...changes
  }

  const option = getOption(component, choice.optionId)

  if (option.custom_color) {
    choice.ral = choice.ral || DEFAULT_RAL
  } else {
    choice.ral = null
  }
  if (!option.logo_placement) {
    choice.logo = null
  }
  if (!option.logo_placement_right) {
    choice.logo_right = null
  }
  if (!option.logo_placement_left) {
    choice.logo_left = null
  }
  if (!option.logo_placement_front) {
    choice.logo_front = null
  }

  // Find position of choice in choices array
  let idx = -1
  for (let i = 0; i < choices.length; i++) {
    if (choices[i].componentId === componentId) {
      idx = i
      break
    }
  }

  // Insert/replace new/updated choice
  if (idx === -1) {
    choices.push(choice)
  } else {
    choices[idx] = choice
  }

  // Recursively apply any requirements
  const { requirements } = option
  if (requirements) {
    for (let otherComponentId in requirements) {
      const otherChoice = choices.find(
        (c) => c.componentId === otherComponentId
      )
      const validOptionIds = requirements[otherComponentId]
      if (!otherChoice || !validOptionIds.includes(otherChoice.optionId)) {
        putChoiceHelper(
          choices,
          baseModel,
          otherComponentId,
          { optionId: validOptionIds[0] },
          stack
        )
      }
    }
  }

  // Pop the stack for higher callers
  stack.pop()
}

export function putAllChoiceColors(bikeConfig, newRal) {
  const baseModel = getBaseModel(bikeConfig.baseModelId)
  let choices = bikeConfig.choices.map((choice) => {
    let component = getComponent(baseModel, choice.componentId)
    let option = getOption(component, choice.optionId)
    let ral = null
    if (option.custom_color) {
      ral = newRal
    }
    if (option.id === 'unpainted') {
      ral = 2
    }

    return {
      ...choice,
      ral
    }
  })
  return updateConfig(bikeConfig, {
    choices
  })
}

export function putAngle(bikeConfig, angle) {
  return updateConfig(bikeConfig, {
    angle
  })
}

export function putBackgroundImage(bikeConfig, url) {
  return updateConfig(bikeConfig, {
    background: {
      ...bikeConfig.background,
      image: url
    }
  })
}

export function deleteBackgroundImage(bikeConfig) {
  return putBackgroundImage(bikeConfig, null)
}

export function putBackgroundMode(bikeConfig, mode) {
  return updateConfig(bikeConfig, {
    background: {
      ...bikeConfig.background,
      mode
    }
  })
}

export function putBackgroundColor(bikeConfig, color) {
  return updateConfig(bikeConfig, {
    background: {
      ...bikeConfig.background,
      color
    }
  })
}

function updateConfig(bikeConfig, changes) {
  return {
    ...bikeConfig,
    ...changes
  }
}

export function getOrderStatement(
  bikeConfig,
  quantity = 1,
  currencyCode = 'DKK'
) {
  const lines = []

  // Add cost of base model
  const baseModel = getBaseModel(bikeConfig.baseModelId)
  lines.push({
    label: getTranslation(baseModel.name),
    amount: baseModel.prices[currencyCode]
  })

  // Consider each choice
  let customColorCount = 0
  bikeConfig.choices.forEach((choice) => {
    const component = getComponent(baseModel, choice.componentId)
    const option = getOption(component, choice.optionId)

    // Add cost of option
    if (option.price) {
      lines.push({
        label:
          getTranslation(component.name) + ': ' + getTranslation(option.name),
        amount: option.prices[currencyCode]
      })
    }

    // Count how many custom colors we have and add the custom color price if > 0
    if (option.custom_color) {
      const color = getColor(choice.ral)
      if (!color.standard) {
        customColorCount++

        // Only add the custom color price once for the whole bike
        if (customColorCount === 1) {
          lines.push({
            label: t('custom_color'),
            amount: getCustomColorPrice(currencyCode, bikeConfig.baseModelId)
          })
        }
      }
    }

    const logoCount =
      (option.logo_placement && choice.logo ? 1 : 0) +
      (option.logo_placement_right && choice.logo_right ? 1 : 0) +
      (option.logo_placement_left && choice.logo_left ? 1 : 0) +
      (option.logo_placement_front && choice.logo_front ? 1 : 0)

    if (logoCount > 0) {
      lines.push({
        label: t('logo') + ' ' + getTranslation(option.name),
        amount:
          choice.componentId === 'electric-motor'
            ? getElechicLogoPrice() * logoCount
            : getLogoPrice(currencyCode) * logoCount
      })
    }
  })

  const unitPrice = lines.reduce((memo, { amount }) => memo + amount, 0)
  const amount = quantity * unitPrice
  return {
    quantity,
    lines,
    unitPrice,
    amount,
    customColorCount
  }
}

export const checkLack = (prompt) => {
  if (prompt) {
    const component = Object.keys(prompt)
    let lack = true
    prompt[component].forEach((choice) => {
      const check = `${component}=${choice}`
      if (window.location.href.search(check) > 0) {
        lack = false
      }
    })
    return lack
  }
  return false
}
